import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@components/atoms/button';
import { ButtonCopyText } from '@components/atoms/button-copy-text';
import { Link } from '@components/atoms/link';
import { Typography } from '@components/atoms/typography';
import { Carousel } from '@components/moleculs/carousel';
import { Checkbox } from '@components/moleculs/checkbox';
import { CountryFlag } from '@components/moleculs/country-flag';
import { LabeledField } from '@components/moleculs/labeled-field';
import { TextField } from '@components/moleculs/text-field';
import { LanguageSelector } from '@components/organisms/language-selector';
import { useCountryOptions } from '@hooks/options/use-country-options';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import { DocsVerificationStepEnum } from '@models/enums';
import type { ThemeProps } from '@models/types';
import { RequirementTypeEnum } from '@modules/dashboard/pages/docs-verification/docs-verification-page/requirements/types';
import Box from '@mui/material/Box';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { scrollTop } from '@utils/util';

import { useSelectedChecks } from '../../hooks/use-selected-checks';
import { QrLink } from '../../qr-link';
import { Requirements } from '../../requirements';
import { RequirementsButton } from '../styles';

import { Card } from './card';
import { CardsContainer, ConfirmationContainer, LogoWrapper, StartStepContainer } from './styles';
import { Timer } from './timer';

export const StartStep: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const dispatch = useDispatch();
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const isTermsAccepted = useSelector(
        (state: RootState) => state.docsVerification.isTermsAccepted,
    );

    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const matchesMobileMd = useMediaQuery(`(max-width:${theme.breakPoints.md})`);
    const { height } = useScreenFullHeight();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [selectedCardRequirements, setSelectedCardRequirements] =
        useState<RequirementTypeEnum | null>(null);

    const { isHasProofAddress, isHasPersonalData, isHasLiveness, isHasPoI } = useSelectedChecks();
    const [isTermsConfirmation, setIsTermsConfirmation] = useState<boolean>(() => isTermsAccepted);

    const countryOptions = useCountryOptions();

    const handleStartVerificationClock = async () => {
        if (sdkOptions) {
            dispatch(docsVerificationSlice.actions.setIsTermsAccepted(true));
            dispatch(
                docsVerificationSlice.actions.setCurrentStep(DocsVerificationStepEnum.VERIFICATION),
            );
            setTimeout(() => {
                scrollTop();
            });
        }
    };

    const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsTermsConfirmation(e.target.checked);
    };

    const sdkCountry = countryOptions.find((opt) => opt.value === sdkOptions?.country)?.label || '';

    const handleRequirementsClick = useCallback((slide: number) => {
        setSelectedCardRequirements(slide);
        setTimeout(() => {
            scrollTop();
        });
    }, []);

    const cards = useMemo(() => {
        let number = 0;

        const result = [];

        if (isHasPersonalData) {
            number += 1;
            result.push({
                icon: 'https://static.dataspike.io/images/sdk/verification/complete-personal-data.png',
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.completePersonalData')}</span>
                    </div>
                ),
            });
        }

        if (isHasPoI) {
            number += 1;
            result.push({
                icon: 'https://static.dataspike.io/images/sdk/verification/passport.png',
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>
                            {t('docVerificationWidget.start.prepareIdentityDocumentMobile')}
                        </span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.PASSPORT);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        if (isHasLiveness) {
            number += 1;
            result.push({
                icon: 'https://static.dataspike.io/images/sdk/verification/selfie.png',
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.prepareSelfie')}</span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.SELFIE);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        if (isHasProofAddress) {
            number += 1;
            result.push({
                icon: 'https://static.dataspike.io/images/sdk/verification/poa.png',
                number: Number(number),
                description: (
                    <div className='requirements-container'>
                        <span>{t('docVerificationWidget.start.prepareUtilityBill')}</span>
                        <RequirementsButton
                            variant='ghost'
                            onClick={() => {
                                handleRequirementsClick(RequirementTypeEnum.POA);
                            }}
                        >
                            {t('common.requirements')}
                        </RequirementsButton>
                    </div>
                ),
            });
        }

        return result;
    }, [t, handleRequirementsClick, isHasPersonalData, isHasPoI, isHasProofAddress, isHasLiveness]);

    const handleSlideChange = (num: number) => {
        setCurrentSlide(num);
    };

    const handleBackClick = () => {
        setSelectedCardRequirements(null);
    };

    const domain = useSelector((state: RootState) => state.domain);
    const {
        settings: { logo_settings, other_settings, domain_settings },
    } = domain;
    const logoLongSrc = logo_settings.logo_wide_url;
    const termsLink =
        other_settings.terms_of_service_url || `https://${domain_settings.dash}/policy/terms`;
    const privacyLink =
        other_settings.privacy_policy_url || `https://${domain_settings.dash}/policy/privacy`;

    const startVerificationDisabled = !isTermsConfirmation;

    const link = sdkOptions?.isDemo ? window.location.href : sdkOptions?.verificationUrl || '';

    return (
        <StartStepContainer>
            <div
                className='content-wrapper'
                style={{ height: matchesMobileSm && height ? height - 48 : undefined }}
            >
                {selectedCardRequirements === null ? (
                    <div className='main-content-container'>
                        <div className='logo-container'>
                            <LanguageSelector className='language-selector' />
                            {!!logoLongSrc && (
                                <LogoWrapper
                                    style={
                                        theme.components?.docVerWidget?.startStep?.logoContainer
                                            ?.style
                                    }
                                >
                                    {logoLongSrc && (
                                        <img
                                            src={logoLongSrc}
                                            className='logo-wrapper'
                                            alt='logo'
                                            style={
                                                theme.components?.docVerWidget?.startStep?.logo
                                                    ?.style
                                            }
                                        />
                                    )}
                                </LogoWrapper>
                            )}
                        </div>
                        <Typography tag='h3' variant='h2' color='text-main' className='title'>
                            {t('docVerificationWidget.start.title')}
                        </Typography>
                        <Timer />
                        {matchesMobileMd ? (
                            <Carousel
                                autoPlay={false}
                                onChange={handleSlideChange}
                                index={currentSlide}
                                className='carousel'
                            >
                                {cards.map((card) => (
                                    <Card key={card.number} {...card} />
                                ))}
                            </Carousel>
                        ) : (
                            <CardsContainer className='cards-container'>
                                {cards.map((card) => (
                                    <Card key={card.number} {...card} />
                                ))}
                            </CardsContainer>
                        )}
                        {matchesMobileMd ? null : (
                            <div className='links-container'>
                                <div className='card'>
                                    <QrLink link={link} />
                                </div>
                                <div className='card'>
                                    <LabeledField
                                        label={t('common.copyLink')}
                                        className='copy-link-title'
                                    >
                                        <TextField
                                            disabled={true}
                                            fullWidth={true}
                                            value={link}
                                            InputProps={{
                                                endAdornment: (
                                                    <ButtonCopyText
                                                        copyText={sdkOptions?.verificationUrl || ''}
                                                        tooltipProps={{
                                                            title: t('common.copied'),
                                                            placement: 'bottom',
                                                        }}
                                                        className='icon'
                                                    />
                                                ),
                                            }}
                                            inputSize='m'
                                        />
                                    </LabeledField>
                                </div>
                            </div>
                        )}
                        <div className='footer-container'>
                            {sdkOptions?.country && (
                                <LabeledField
                                    label={t('docVerificationWidget.start.selectedCountry')}
                                >
                                    <Box
                                        display='flex'
                                        alignItems='center'
                                        gap='8px'
                                        fontSize='14px'
                                    >
                                        <CountryFlag name={sdkOptions.country} />
                                        <span>{sdkCountry}</span>
                                    </Box>
                                </LabeledField>
                            )}

                            <ConfirmationContainer className='confirmation-container'>
                                <Checkbox
                                    checked={isTermsConfirmation}
                                    onChange={handleTermsChange}
                                    label={
                                        <Trans
                                            i18nKey='docVerificationWidget.start.terms'
                                            components={{
                                                link1: (
                                                    // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/control-has-associated-label
                                                    <Link
                                                        href={termsLink || ''}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    />
                                                ),
                                                link2: (
                                                    // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/control-has-associated-label
                                                    <Link
                                                        href={privacyLink || ''}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    />
                                                ),
                                            }}
                                        />
                                    }
                                />
                            </ConfirmationContainer>
                            <Button
                                variant='primary'
                                className='button'
                                onClick={handleStartVerificationClock}
                                disabled={startVerificationDisabled}
                                size='xl'
                            >
                                {t('docVerificationWidget.start.btnStartVerification')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Requirements type={selectedCardRequirements} onBack={handleBackClick} />
                )}
            </div>
        </StartStepContainer>
    );
};
